import styles from './TopBar.module.scss';
import {NavLink, useHistory} from 'react-router-dom';
import Button from 'components/Button';
import toast from 'react-hot-toast';
import useController from './useController';
import classNames from 'classnames';
import MenuIcon from 'components/Sidebar/MenuIcon';
import {useEffect, useMemo, useRef, useState} from 'react';
import {get} from 'lodash';
import {CompanyType, ListResponse, MenuItemName, User} from 'types';
import useAuth from 'hooks/useAuth';
import {ChevronDownIcon} from './icons/chevron-down';
import Avatar from 'components/Avatar';
import UserModal from 'components/Sidebar/UserModal';
import {getFullName} from 'helpers/user';
import {QueryFunctionContext, useQuery} from 'react-query';
import api from 'api';
import Select from 'components/Select';
import {components} from 'react-select';
import Typography from 'components/Typography';
import Logo from 'components/Logo';
import useCompanyConfig from 'hooks/useCompanyConfig';
// import ButtonWithIcon from 'components/ButtonWithIcon';
import {NotificationPopup} from './components';

interface SubmenuItem {
  title: string;
  path: string;
  exact?: boolean;
  route: string;
}
interface MenuItem {
  name: MenuItemName;
  title: string;
  path: string;
  exact?: boolean;
  subitems?: SubmenuItem[];
  route?: string;
}

async function getCompanies({queryKey}: QueryFunctionContext<any[]>) {
  const [, isAdmin] = queryKey;
  if (!isAdmin) return;
  const {data} = await api.get<ListResponse<User['company']>>(
    '/companies/companies'
  );
  return data?.results;
}

const getCompanyTypeLabel = (companyType: CompanyType) => {
  if (companyType === 'property_manager') return ` - PM`;
  if (companyType === 'service_provider') return ` - SP`;
  if (companyType === 'delivery_manager') return ` - DM`;
  if (companyType === 'msp_provider') return ` - MSP`;
  if (companyType === 'pickup_dropoff') return ` - R`;
  if (companyType === 'insurance_adjuster') return ` - IA`;
  return '';
};

function TopBar() {
  const {push} = useHistory();
  const {hasOrder, pathname} = useController();
  const {currentUser, rolePermission, refetch} = useAuth();
  const [showSubmenu, setShowSubmenu] = useState(false);
  const [selectedMenu, setSelectedMenu] = useState<MenuItem | null>(null);
  const [submenuPosition, setSubmenuPosition] = useState({left: 0, top: 0});
  const menuRef = useRef<HTMLDivElement>(null);
  const [isUserModalOpen, setIsUserModalOpen] = useState(false);
  const isAdmin = currentUser?.role === 'superuser';
  const {data: companies} = useQuery(['companies', isAdmin], getCompanies, {
    enabled: isAdmin,
  });
  const currentCompany = currentUser?.company;
  const {isDeliveryManager, labels, isPickupDropoff} = useCompanyConfig();
  const companyOptions = useMemo(() => {
    return companies?.map((company: User['company']) => ({
      type: company?.type,
      label: company?.name!,
      value: company?.id!,
    }));
  }, [companies]);
  const goToOrders = () => {
    toast.success('Please select the orders you would like to dispatch', {
      duration: 3000,
    });
    push('/orders');
  };
  const MENU_ITEMS: MenuItem[] = useMemo(
    () => [
      {
        name: isDeliveryManager ? 'rx' : 'squareChartGantt',
        title: labels.orders,
        path: '/orders',
        route: 'orders',
      },
      {
        name: 'truck',
        title: 'Dispatch',
        path: '/deliveries/delivery_planner',
        route: 'dispatch',
      },
      {
        name: 'transactions',
        title: 'Invoices',
        path: '/transactions',
        exact: false,
        route: 'transactions',
      },

      {
        name: 'members',
        title: 'People',
        path: '/people',
        route: 'staff',
        subitems: [
          {
            title: 'Staff',
            path: '/people/employees',
            route: 'staff',
          },
          {
            title: 'Vendors',
            path: '/people/vendors',
            route: 'vendors',
          },
          {
            title: 'Customers',
            path: '/people/customers',
            route: 'customers',
          },
          {
            title: labels.workers,
            path: '/people/teams',
            route: 'onsite_workers',
          },
        ],
      },
      {
        name: 'site',
        title: 'Website',
        path: '/website',
        subitems: [
          {title: 'Blogs', path: '/website/blogs', route: 'website'},
          {title: 'Pages', path: '/website/pages', route: 'website'},
        ],
        route: 'website',
      },
      {
        name: 'gateway',
        route: 'integrations',
        title: 'Settings',
        subitems: [
          {
            title: 'Offerings',
            path: '/products',
            route: 'offerings',
          },
          {
            route: 'billing',
            title: 'Billing',
            path: get(currentUser, 'company.plan')
              ? '/settings/billing'
              : '/settings/plans',
          },
          {
            title: 'Integrations',
            path: '/settings/integrations',
            route: 'integrations',
          },
          {title: 'Company', path: '/settings/company', route: 'company'},
          {
            title: 'SMS Templates',
            path: '/settings/templates',
            route: 'templates',
          },
        ],
        path: '/settings',
      },
    ],
    [currentUser, isDeliveryManager, labels]
  );

  useEffect(() => {
    const handlePopState = (event: PopStateEvent) => {
      event.preventDefault();
      setSelectedMenu(null);
    };

    window.addEventListener('popstate', handlePopState);
    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, []);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setShowSubmenu(false);
      }
    };

    if (showSubmenu) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showSubmenu]);

  const hasReadPermission = useMemo(() => {
    return (route: string) => {
      const permission = rolePermission[route];
      return permission ? permission.permissions.read : true;
    };
  }, [rolePermission]);

  // Optimized menu filtering with memoization
  const filteredMenuItems = useMemo(() => {
    return MENU_ITEMS.map((item) => {
      if (!isPickupDropoff && item.name === 'members') {
        return {
          ...item,
          subitems: item.subitems?.filter(
            (subitem) => subitem.route !== 'vendors'
          ),
        };
      }
      if (item.subitems) {
        return {
          ...item,
          subitems: item.subitems.filter((subitem) =>
            hasReadPermission(subitem.route)
          ),
        };
      }
      return item;
    }).filter((item) => hasReadPermission(item.route as string));
  }, [MENU_ITEMS, hasReadPermission, isPickupDropoff]);

  const hideDispatch =
    (pathname === '/deliveries/delivery_planner' && hasOrder) ||
    pathname.startsWith('/orders');

  const handleToggleSubmenu = (item: MenuItem) => (event: React.MouseEvent) => {
    const buttonElement = event.currentTarget as HTMLElement;
    const rect = buttonElement.getBoundingClientRect();
    const topbarRect = menuRef.current?.getBoundingClientRect();

    if (topbarRect) {
      setSubmenuPosition({
        left: rect.left - topbarRect.left, // Center under the button
        top: rect.bottom - topbarRect.top,
      });
    }

    setSelectedMenu(item);
    setShowSubmenu(item.name === selectedMenu?.name ? !showSubmenu : true);
  };

  const changeCompany = async (selectedCompanyId: string) => {
    try {
      await api.patch(`/users/${currentUser?.id}/`, {
        company: selectedCompanyId,
      });
      refetch(true);
    } catch (error) {
      console.error(error);
    }
  };
  const handleChangeCompany = async (companyId: string) => {
    sessionStorage.clear();
    await changeCompany(companyId);
  };
  return (
    <>
      <div className={styles.root} ref={menuRef}>
        <div
          className={classNames(
            styles.brandContainer,
            !isAdmin && styles.noCompanySelect
          )}
        >
          <div
            className={styles.brand}
            onClick={() => push('/deliveries/delivery_planner')}
          >
            <div className={styles.logo}>
              <Logo
                style={{
                  width: 32,
                  height: 32,
                }}
              />
            </div>
            <span className={styles.brandName}>Magic</span>
          </div>

          {isAdmin && (
            <Select
              defaultValue={
                currentCompany
                  ? {label: currentCompany?.name!, value: currentCompany?.id!}
                  : null
              }
              placeholder="Select company"
              className={styles.select}
              options={companyOptions}
              onChange={(option) =>
                handleChangeCompany(option?.value as string)
              }
              components={{
                Option: ({children, ...props}) => (
                  <components.Option {...props}>
                    {children}
                    <Typography variant="label" textAlign="right">
                      {getCompanyTypeLabel((props.data as any)?.type)}
                    </Typography>
                  </components.Option>
                ),
                SelectContainer: ({children, ...props}) => (
                  <components.SelectContainer {...props}>
                    {children}
                  </components.SelectContainer>
                ),
              }}
            />
          )}
        </div>

        <nav className={styles.nav}>
          <ul className={styles.menu}>
            {filteredMenuItems.map((item, index) => (
              <li className={classNames(styles.menu_item)} key={item.name}>
                {!!item?.subitems ? (
                  <>
                    <button
                      style={{position: 'relative'}}
                      className={
                        item.name === selectedMenu?.name ? styles.active : ''
                      }
                      onClick={handleToggleSubmenu(item)}
                      aria-label="Toggle submenu"
                    >
                      <MenuIcon
                        className={styles.menu_item__icon}
                        name={item.name}
                      />
                      <span className={styles.text}>{item.title}</span>
                      {item.subitems && (
                        <span className={styles.subitems}>
                          <ChevronDownIcon className={styles.subitems__icon} />
                        </span>
                      )}
                    </button>
                  </>
                ) : (
                  <>
                    <NavLink
                      to={item.path}
                      activeClassName={!selectedMenu ? styles.active : ''}
                      exact={item.exact}
                      style={{position: 'relative'}}
                      onClick={() => setSelectedMenu(null)}
                    >
                      <MenuIcon
                        className={styles.menu_item__icon}
                        name={item.name}
                      />
                      <span className={styles.text}>{item.title}</span>
                      {item.subitems && (
                        <span className={styles.subitems}>
                          <ChevronDownIcon className={styles.subitems__icon} />
                        </span>
                      )}
                    </NavLink>
                  </>
                )}
              </li>
            ))}
          </ul>
        </nav>

        {/* Submenu rendered outside nav to avoid clipping */}
        {showSubmenu && selectedMenu?.subitems && (
          <ul
            className={styles.subMenu}
            style={{
              left: `${submenuPosition.left}px`,
              top: `${submenuPosition.top}px`,
            }}
          >
            {selectedMenu.subitems?.map((subitem) => (
              <li
                className={styles.subMenu__item}
                key={subitem.path}
                onClick={() => setShowSubmenu(false)}
              >
                <NavLink
                  to={subitem.path}
                  activeClassName={styles.active}
                  exact={subitem.exact}
                >
                  {subitem.title}
                </NavLink>
              </li>
            ))}
          </ul>
        )}

        <div className={styles.buttonContainer}>
          <NotificationPopup />
          {!hideDispatch && (
            <Button
              onClick={goToOrders}
              className={styles.button}
              color="primary"
            >
              Start Dispatching
            </Button>
          )}
          <div className={styles.user_wrapper}>
            <div
              className={styles.user}
              onClick={() => setIsUserModalOpen(true)}
            >
              <Avatar
                className={styles.user__avatar}
                file={currentUser?.avatar}
              />
              <div className={styles.user__details}>
                <span className={styles.user__name}>
                  {getFullName(currentUser)}
                </span>
                <span className={styles.user__email}>{currentUser?.email}</span>
              </div>
            </div>
          </div>
          <UserModal
            isOpen={isUserModalOpen}
            onClose={() => setIsUserModalOpen(false)}
          />
        </div>
      </div>
    </>
  );
}

export default TopBar;
